import type {Editor} from 'grapesjs';
import {EMAIL_MAKER_PROPS} from '@EmailMakerReact/email-maker-react.constants';
import {logEmailMakerError} from '@EmailMakerReact/lib/email-maker-error-library';
import type {EmailUploadImageResponse} from '@EmailMakerReact/lib/email-maker-media-library';
import {setImageLoading, updateLoadingImageWithResponse} from '@EmailMakerReact/lib/email-maker-media-library';
import {getComponentByLoadIdentifier} from '@EmailMakerReact/lib/email-maker-component-query-library';

const UPLOAD_ENDPOINT = 'posterimage/emailUpload';
const UPLOAD_FILE_PARAM = 'Filedata';

interface AssetUploadResponse {
  data: EmailUploadImageResponse;
}

type FileWithDropIdentifier = File & Partial<Record<typeof EMAIL_MAKER_PROPS.DROP_IDENTIFIER, string>>;

export function initStorageListeners(editor: Editor): () => void {
  const assetsConfig = editor.AssetManager.getConfig();
  const previousUploadFile = assetsConfig.uploadFile;

  assetsConfig.uploadFile = (e: DragEvent): void => {
    const files = getFilesFromUploadEvent(e);
    if (!files) {
      return;
    }

    for (const file of files) {
      uploadEmailAsset(editor, file);
    }
  };

  const onAssetUploadResponse = (response: string): void => {
    let result: AssetUploadResponse;
    try {
      result = JSON.parse(response) as AssetUploadResponse;
    } catch (error: unknown) {
      logEmailMakerError('asset:upload:response — failed to parse response', error);
      return;
    }
    editor.AssetManager.add(result.data);
  };

  editor.on('asset:upload:response', onAssetUploadResponse);

  return (): void => {
    assetsConfig.uploadFile = previousUploadFile;
    editor.off('asset:upload:response', onAssetUploadResponse);
  };
}

function getFilesFromUploadEvent(e: DragEvent): FileList | null {
  if (e.dataTransfer) {
    return e.dataTransfer.files;
  }

  return e.target instanceof HTMLInputElement ? e.target.files : null;
}

/** Uploads one dropped/picked file and swaps the drop placeholder with the uploaded image. */
function uploadEmailAsset(editor: Editor, file: FileWithDropIdentifier): void {
  const formData = new FormData();
  formData.append(UPLOAD_FILE_PARAM, file);
  const identifier = file[EMAIL_MAKER_PROPS.DROP_IDENTIFIER];

  window.PMW.write(window.PMW.util.site_url(UPLOAD_ENDPOINT), formData)
    .then((result: AssetUploadResponse): void => {
      const image = result.data;
      editor.AssetManager.add(image);

      if (identifier) {
        updateLoadingImageWithResponse(editor, identifier, image);
      }
    })
    .catch((error: unknown) => {
      logEmailMakerError('Upload failed', error);
      // Unstick the dropped placeholder's loading state.
      if (identifier) {
        setImageLoading(editor, {component: getComponentByLoadIdentifier(editor, identifier), loading: false});
      }
    });
}
